import {
  Briefcase,
  FileHeart,
  Linkedin,
  MessageCircleQuestion,
  Sparkles,
  Star,
} from 'lucide-react';

import CandidateScore from '@/modules/candidates/components/CandidateScore';
import { WorkExperienceTimeline } from '@/modules/candidates/components/WorkExperienceTimeline';
import type { CandidateJobApi } from '@/modules/candidates/types';
import { getCandidateDisplayScore } from '@/modules/candidates/utils/candidateAssessment';
import { Avatar, AvatarFallback } from '@/modules/ui/components/avatar/Avatar';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Callout,
  CalloutDescription,
  CalloutTitle,
} from '@/modules/ui/components/callout/Callout';
import { Card } from '@/modules/ui/components/card/Card';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Separator } from '@/modules/ui/components/separator/Separator';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { ellipseText } from '@/utils/ellipseText';
import { getInitials } from '@/utils/getInitials';

interface CandidateDetailProps {
  candidateJob: CandidateJobApi;
  additionalActions?: React.ReactNode;
}

type CandidateWithSkills = CandidateJobApi['candidate'] & {
  skills?: string[];
};

export function CandidateDetail({
  candidateJob,
  additionalActions,
}: CandidateDetailProps) {
  const candidate = candidateJob.candidate;
  const fullName = candidate
    ? `${candidate.firstName} ${candidate.lastName}`
    : '';
  const candidateSkills =
    (candidate as CandidateWithSkills)?.skills?.filter(Boolean).slice(0, 16) ??
    [];

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar className="bg-primary size-12 text-lg text-white">
            <AvatarFallback>
              {getInitials(
                `${candidate?.firstName ?? ''} ${candidate?.lastName ?? ''}`.trim(),
              )}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <Subheading variant="h4" className="text-lg font-semibold">
              {fullName}
            </Subheading>
            <div className="flex items-center gap-2 text-neutral-500">
              {candidate?.headline && (
                <span>{ellipseText(candidate.headline, 100)}</span>
              )}
              {candidate?.headline && candidate?.location && (
                <Separator orientation="vertical" className="h-4" />
              )}
              {candidate?.location && <span>{candidate.location}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <CandidateScore score={getCandidateDisplayScore(candidateJob)} />
          {candidate?.linkedInProfileId && (
            <Button
              variant="outline"
              onClick={() =>
                window.open(
                  `https://www.linkedin.com/in/${candidate.linkedInProfileId}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
              className="inline-flex items-center gap-2"
            >
              <Linkedin size={18} />
              <span>LinkedIn</span>
            </Button>
          )}
          {additionalActions}
        </div>
      </div>

      <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:justify-between">
        <div className="flex flex-1 flex-col gap-10">
          {candidate?.summary && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Sparkles size={19} />
                <Subheading variant="h4" className="text-lg font-semibold">
                  AI Summary
                </Subheading>
              </div>
              <p className="text-sm text-neutral-600">{candidate.summary}</p>
            </div>
          )}

          {candidateSkills.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Sparkles size={19} />
                <Subheading variant="h4" className="text-lg font-semibold">
                  Skills
                </Subheading>
              </div>
              <div className="flex flex-wrap gap-2">
                {candidateSkills.map((skill) => (
                  <Badge key={skill} variant="outline" size="sm">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {candidateJob.justification && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <FileHeart size={19} />
                <Subheading variant="h4" className="text-lg font-semibold">
                  AI Match Justification
                </Subheading>
              </div>
              <p className="text-sm text-neutral-600">
                {candidateJob.justification}
              </p>
            </div>
          )}

          {candidateJob.strengths && candidateJob.strengths.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Star size={19} />
                <Subheading variant="h4" className="text-lg font-semibold">
                  Key Strengths
                </Subheading>
              </div>
              <div className="flex flex-col gap-3">
                {candidateJob.strengths.map((strength, index) => (
                  <Callout key={index} variant="success">
                    <CalloutTitle>{strength.summary}</CalloutTitle>
                    <CalloutDescription>
                      {strength.explanation}
                    </CalloutDescription>
                  </Callout>
                ))}
              </div>
            </div>
          )}

          {candidateJob.clarifications &&
            candidateJob.clarifications.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <MessageCircleQuestion size={19} />
                  <Subheading variant="h4" className="text-lg font-semibold">
                    Clarifications Needed
                  </Subheading>
                </div>
                <div className="flex flex-col gap-3">
                  {candidateJob.clarifications.map((clarification, index) => (
                    <Callout key={index} variant="warning">
                      <CalloutTitle>{clarification.summary}</CalloutTitle>
                      <CalloutDescription>
                        {clarification.explanation}
                      </CalloutDescription>
                    </Callout>
                  ))}
                </div>
              </div>
            )}
        </div>

        <div className="flex flex-1 flex-col gap-4">
          <div className="flex items-center gap-2">
            <Briefcase size={19} />
            <Subheading variant="h4" className="text-lg font-semibold">
              Work Experience
            </Subheading>
          </div>
          {candidate?.workExperiences ? (
            <WorkExperienceTimeline
              workExperiences={candidate.workExperiences}
              expandable={false}
            />
          ) : (
            <Paragraph variant="caption">
              No work experience available
            </Paragraph>
          )}
        </div>
      </div>
    </Card>
  );
}
